import { inject, Injectable } from '@angular/core';
import { FILTER_SEARCH_DRIVER } from '@miq/platform';
import { DataObject } from '@miq/shared-models';
import { Subject } from 'rxjs';
/**
 * L2-S5: this service used to `inject(RecordsSearchStateService)`, and that single edge dragged
 * 109 host files into every bundle containing a range item or a filter option — i.e. into the
 * whole `filters-bar` family and `more-filters`. It only ever touched two members of the state
 * service, so it now depends on the {@link FILTER_SEARCH_DRIVER} contract instead.
 *
 * The token is provided (aliasing `RecordsSearchStateService`) at every site that provides THIS
 * service — `records-search.component`, `space-details.component` and `lease-expiry.routes` — so
 * the driver resolved here is the same instance `inject()` used to return, including the
 * route-scoped one under lease-expiry. `multi-equal-item.component` does not provide either; it
 * inherits both from whichever of those ancestors is rendering it, exactly as before.
 *
 * The injection is `{ optional: true }` because the reports remote renders filter bars that do not
 * drive a records search (report-list, corporate-filter, finalize-historical-data all consume
 * `(selection)` themselves), so it provides no driver and these two members degrade to a no-op /
 * empty params rather than throwing a NullInjectorError.
 */
@Injectable()
export class FilterOptionService {
  private readonly clearNullFilter = new Subject<void>();
  clearNullFilter$ = this.clearNullFilter.asObservable();
  private readonly searchDriver = inject(FILTER_SEARCH_DRIVER, { optional: true });
  get searchParams(): DataObject {
    // `{}` (not undefined) when there is no driver: every consumer destructures `{ filters }` off
    // this, so a bare undefined would be a TypeError instead of an absent filter list.
    return this.searchDriver?.activeQueryState ?? {};
  }
  searchQuery(filters) {
    this.searchDriver?.setQueryAndSearch({ ...this.searchParams, filters: filters });
  }
  clearNullFilters() {
    this.clearNullFilter.next();
  }
}
